// TanStack Query hooks — one per /v1 endpoint. A save invalidates the derived
// queries (dashboard/projection/montecarlo) so the recompute + headline refresh.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  Account,
  AccountInput,
  AccountsResponse,
  Assumptions,
  AssumptionsInput,
  DashboardResponse,
  MonteCarloRequest,
  MonteCarloResponse,
  RetirementRequest,
  RetirementResponse,
} from "@retirement-planner/contract";
import { api } from "../lib/apiClient";

const keys = {
  dashboard: ["dashboard"] as const,
  assumptions: ["assumptions"] as const,
  accounts: ["accounts"] as const,
  montecarlo: ["montecarlo"] as const,
};

export function useDashboard(): UseQueryResult<DashboardResponse> {
  return useQuery({ queryKey: keys.dashboard, queryFn: ({ signal }) => api.getDashboard(signal) });
}

export function useAssumptions(): UseQueryResult<Assumptions> {
  return useQuery({ queryKey: keys.assumptions, queryFn: api.getAssumptions });
}

export function useAccounts(): UseQueryResult<AccountsResponse> {
  return useQuery({ queryKey: keys.accounts, queryFn: api.listAccounts });
}

export function useMonteCarlo(request: MonteCarloRequest): UseQueryResult<MonteCarloResponse> {
  return useQuery({
    queryKey: [...keys.montecarlo, request.stressPreset ?? "none", request.runs ?? 10000],
    queryFn: () => api.runMonteCarlo(request),
  });
}

function useRecomputeInvalidation() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: keys.dashboard }),
      qc.invalidateQueries({ queryKey: keys.assumptions }),
      qc.invalidateQueries({ queryKey: keys.montecarlo }),
    ]);
}

export function useSaveAssumptions() {
  const invalidate = useRecomputeInvalidation();
  return useMutation<Assumptions, Error, AssumptionsInput>({
    mutationFn: (body) => api.putAssumptions(body),
    onSuccess: invalidate,
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  const invalidate = useRecomputeInvalidation();
  return useMutation<Account, Error, AccountInput>({
    mutationFn: (body) => api.createAccount(body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.accounts });
      await invalidate();
    },
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation<Account, Error, { id: string; body: AccountInput }>({
    mutationFn: ({ id, body }) => api.updateAccount(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.accounts }),
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.deleteAccount(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.accounts }),
  });
}

/** Authoritative what-if commit (label flips Preview -> Confirmed). Abortable. */
export async function commitRetirement(
  body: RetirementRequest,
  signal?: AbortSignal,
): Promise<RetirementResponse> {
  return api.solveRetirement(body, signal);
}
